import { useState, useEffect } from 'react';
import { COLOR_HEX, Color, BotDifficulty } from '@otrio/engine';
import { LobbyState, LobbySlot, ClientMessage } from '../protocol';
import { COLOR_LABELS } from '../colorLabels';

interface LobbyRoomProps {
  lobby: LobbyState;
  yourColor: Color;
  /** All colors this device controls (may be more than one on a shared device). */
  localColors: Color[];
  roomCode: string;
  onSendMessage: (msg: ClientMessage) => void;
  onClaimColor?: (color: Color, name: string) => void;
  /** if true we came back from a finished game */
  isPostGame?: boolean;
  onExit?: () => void;
}

export function LobbyRoom({ lobby, yourColor, localColors, roomCode, onSendMessage, onClaimColor, isPostGame, onExit }: LobbyRoomProps) {
  const isCreator = localColors.includes(lobby.creatorColor);
  const mySlot = lobby.slots.find(s => s.color === yourColor);
  const [joinName, setJoinName] = useState('');
  const [joinTarget, setJoinTarget] = useState<Color | null>(null);
  const [addName, setAddName] = useState('');
  const [addTarget, setAddTarget] = useState<Color | null>(null);
  const [copied, setCopied] = useState(false);

  // Slots not controlled by this device that are claimable
  const joinableSlots = lobby.slots.filter(s =>
    !localColors.includes(s.color) && (s.type === 'open' || s.type === 'bot')
  );

  const shareUrl = (() => {
    const u = new URL(window.location.href);
    u.search = `?join=${roomCode}`;
    return u.toString();
  })();

  const canStart = lobby.slots.filter(s => s.type !== 'open').length >= 2;

  function copyCode() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handlePlayerCountChange(count: 2 | 3 | 4) {
    onSendMessage({ type: 'SET_PLAYER_COUNT', roomCode, count });
  }

  function handleToggleSlot(slot: LobbySlot) {
    if (slot.type === 'bot') {
      onSendMessage({ type: 'UPDATE_SLOT', roomCode, color: slot.color, change: 'set-open' });
    } else if (slot.type === 'open') {
      onSendMessage({ type: 'UPDATE_SLOT', roomCode, color: slot.color, change: 'set-bot' });
    }
  }

  function handleDifficulty(slot: LobbySlot, difficulty: BotDifficulty) {
    onSendMessage({ type: 'UPDATE_SLOT', roomCode, color: slot.color, change: 'set-difficulty', botDifficulty: difficulty });
  }

  function handleKick(slot: LobbySlot) {
    onSendMessage({ type: 'UPDATE_SLOT', roomCode, color: slot.color, change: 'set-bot' });
  }

  function handleClaimSlot() {
    if (!joinTarget || !joinName.trim()) return;
    onSendMessage({ type: 'JOIN_ROOM', roomCode, color: joinTarget, name: joinName.trim() });
    setJoinTarget(null);
    setJoinName('');
  }

  function handleAddLocalPlayer() {
    if (!addTarget || !addName.trim() || !onClaimColor) return;
    onClaimColor(addTarget, addName.trim());
    setAddTarget(null);
    setAddName('');
  }

  function handleReady() {
    if (!mySlot) return;
    // Determine combined ready state from all local human slots
    const localHumanSlots = lobby.slots.filter(s => localColors.includes(s.color) && s.type === 'human');
    const allReady = localHumanSlots.every(s => s.ready);
    onSendMessage({ type: 'SET_READY', roomCode, ready: !allReady });
  }

  function handleStartGame() {
    onSendMessage({ type: 'START_GAME', roomCode });
  }

  return (
    <div className="lobby-room">
      <h1 className="lobby-room-title">{isPostGame ? 'Play Again?' : 'Game Lobby'}</h1>

      {/* Room code + share */}
      <div className="lobby-room-code-row">
        <span className="lobby-room-code-label">Room</span>
        <span className="lobby-room-code">{roomCode}</span>
        <button className="lobby-copy-btn" onClick={copyCode} title="Copy invite link">
          {copied ? '✓ Copied!' : '⧉ Copy Link'}
        </button>
      </div>

      {/* Player count selector (creator only, pre-game) */}
      {isCreator && !isPostGame && (
        <div className="lobby-count-row">
          <span className="lobby-count-label">Players:</span>
          {([2, 3, 4] as const).map(n => (
            <button
              key={n}
              className={`lobby-count-btn${lobby.slots.length === n ? ' active' : ''}`}
              onClick={() => handlePlayerCountChange(n)}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {/* Slot cards */}
      <div className="lobby-slots">
        {lobby.slots.map((slot) => {
          const hex = COLOR_HEX[slot.color];
          const isMe = localColors.includes(slot.color);
          const isHuman = slot.type === 'human';
          const isBot = slot.type === 'bot';
          const isOpen = slot.type === 'open';

          return (
            <div key={slot.color} className={`lobby-slot${isMe ? ' lobby-slot-me' : ''}${isOpen ? ' lobby-slot-open' : ''}`}>
              {/* Color swatch */}
              <div className="lobby-slot-swatch" style={{ background: hex }} />

              {/* Name + status */}
              <div className="lobby-slot-info">
                <span className="lobby-slot-name">
                  {isOpen ? 'Open Slot' : slot.name}
                  {isMe && <span className="lobby-slot-you"> (You)</span>}
                </span>
                <span className={`lobby-slot-badge lobby-slot-badge-${slot.type}`}>
                  {isHuman && slot.connected ? (slot.ready ? '✓ Ready' : 'In Lobby') : ''}
                  {isHuman && !slot.connected ? '⚡ Disconnected' : ''}
                  {isBot ? `🤖 Bot (${slot.botDifficulty})` : ''}
                  {isOpen ? 'Waiting…' : ''}
                </span>
              </div>

              {/* Creator controls */}
              {isCreator && !isMe && !isPostGame && (
                <div className="lobby-slot-controls">
                  {isHuman && slot.connected && (
                    <button className="lobby-slot-kick" onClick={() => handleKick(slot)} title="Remove player">✕</button>
                  )}
                  {(isBot || isOpen) && (
                    <button
                      className={`lobby-slot-toggle${isOpen ? ' open' : ''}`}
                      onClick={() => handleToggleSlot(slot)}
                      title={isBot ? 'Open slot for human' : 'Fill with bot'}
                    >
                      {isBot ? '👤 Open' : '🤖 Bot'}
                    </button>
                  )}
                  {isBot && (
                    <div className="lobby-slot-difficulty">
                      {(['easy', 'hard'] as BotDifficulty[]).map(d => (
                        <button
                          key={d}
                          className={`difficulty-btn${slot.botDifficulty === d ? ' active' : ''}`}
                          onClick={() => handleDifficulty(slot, d)}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Join a slot (first-time visitor who hasn't picked a color yet) */}
      {localColors.length === 0 && joinableSlots.length > 0 && (
        <div className="lobby-join-slot">
          <h3 className="lobby-join-title">Join a Slot</h3>
          <input
            className="lobby-join-name"
            placeholder="Your name"
            value={joinName}
            onChange={e => setJoinName(e.target.value)}
            maxLength={20}
          />
          <div className="lobby-join-colors">
            {joinableSlots.map(slot => (
              <button
                key={slot.color}
                className={`lobby-join-color-btn${joinTarget === slot.color ? ' selected' : ''}`}
                style={{ '--slot-color': COLOR_HEX[slot.color] } as React.CSSProperties}
                onClick={() => setJoinTarget(slot.color)}
                title={COLOR_LABELS[slot.color]}
              >
                <span className="lobby-join-color-dot" style={{ background: COLOR_HEX[slot.color] }} />
                {COLOR_LABELS[slot.color]}
              </button>
            ))}
          </div>
          <button
            className="lobby-join-confirm"
            disabled={!joinTarget || !joinName.trim()}
            onClick={handleClaimSlot}
          >
            Join as {joinTarget ? COLOR_LABELS[joinTarget] : '…'}
          </button>
        </div>
      )}

      {/* Add a local player on this same device */}
      {localColors.length > 0 && joinableSlots.length > 0 && !isPostGame && onClaimColor && (
        <div className="lobby-add-local">
          <h3 className="lobby-add-local-title">Add Local Player</h3>
          <p className="lobby-add-local-hint">Someone else using this device? Add them here.</p>
          <input
            className="lobby-join-name"
            placeholder="Player name"
            value={addName}
            onChange={e => setAddName(e.target.value)}
            maxLength={20}
          />
          <div className="lobby-join-colors">
            {joinableSlots.map(slot => (
              <button
                key={slot.color}
                className={`lobby-join-color-btn${addTarget === slot.color ? ' selected' : ''}`}
                style={{ '--slot-color': COLOR_HEX[slot.color] } as React.CSSProperties}
                onClick={() => setAddTarget(slot.color)}
                title={COLOR_LABELS[slot.color]}
              >
                <span className="lobby-join-color-dot" style={{ background: COLOR_HEX[slot.color] }} />
                {COLOR_LABELS[slot.color]}
              </button>
            ))}
          </div>
          <button
            className="lobby-join-confirm"
            disabled={!addTarget || !addName.trim()}
            onClick={handleAddLocalPlayer}
          >
            Add {addTarget ? COLOR_LABELS[addTarget] : '…'} Locally
          </button>
        </div>
      )}

      {/* Action buttons */}
      <div className="lobby-actions">
        {/* Non-creator human: toggle ready */}
        {!isCreator && mySlot?.type === 'human' && !isPostGame && (() => {
          const localHumanSlots = lobby.slots.filter(s => localColors.includes(s.color) && s.type === 'human');
          const allReady = localHumanSlots.every(s => s.ready);
          return (
            <button
              className={`lobby-ready-btn${allReady ? ' ready' : ''}`}
              onClick={handleReady}
            >
              {allReady ? '✓ Ready!' : 'Ready Up'}
            </button>
          );
        })()}

        {/* Creator: start / play again */}
        {isCreator && !isPostGame && (
          <button
            className="lobby-start-btn"
            disabled={!canStart}
            onClick={handleStartGame}
            title={canStart ? 'Start the game' : 'Need at least 2 players'}
          >
            Start Game
          </button>
        )}

        {isPostGame && isCreator && (
          <button className="lobby-start-btn" disabled={!canStart} onClick={handleStartGame}>
            New Game (same lobby)
          </button>
        )}

        {/* Exit room */}
        {onExit && (
          <button className="lobby-exit-btn" onClick={onExit}>
            Leave Room
          </button>
        )}
      </div>
    </div>
  );
}
